import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {DeliveryNoteActions} from '../actions/deliveryNote.actions';
import {DeliveryNote} from '../models/delivery.note/deliveryNote.model';
import {DeliveryNoteService} from '../services/deliveryNote.service';
import ClearState = Auth.ClearState;
import GetDeliveryNotes = DeliveryNoteActions.GetDeliveryNotes;
import CreateDeliveryNote = DeliveryNoteActions.CreateDeliveryNote;
import DeleteDeliveryNote = DeliveryNoteActions.DeleteDeliveryNote;
import UpdateDeliveryNotes = DeliveryNoteActions.UpdateDeliveryNotes;

export interface DeliveryNoteStateModel {
  deliveryNotes: DeliveryNote[];
  transportCost: number | null;
  disabled: boolean | null;
  id: string | null;
}

@State<DeliveryNoteStateModel>({
  name: 'deliveryNotes',
  defaults: {
    deliveryNotes: [],
    transportCost: null,
    disabled: false,
    id: null,
  }
})
@Injectable()
export class DeliveryNoteState {
  constructor(private deliveryNoteService: DeliveryNoteService
  ) {
  }

  @Selector()
  static deliveryNotes(state: DeliveryNoteStateModel): Array<DeliveryNote> {
    return state.deliveryNotes;
  }

  @Selector()
  static transportCost(state: DeliveryNoteStateModel): number {
    return state.transportCost;
  }

  @Selector()
  static disabled(state: DeliveryNoteStateModel): boolean {
    return state.disabled;
  }

  @Action(GetDeliveryNotes)
  getDeliveryNotes(ctx: StateContext<DeliveryNoteStateModel>, action: GetDeliveryNotes): Observable<DeliveryNote[]> {
    if (!action.force) {
      const deliveryNotes = ctx.getState().deliveryNotes;
      if (deliveryNotes.length > 0 && ctx.getState().id === action.id) {
        return of(deliveryNotes);
      }
    }
    return this.deliveryNoteService.getDeliveryNotes(action.id).pipe(
      tap(jobDeliveryNotes => {
        ctx.patchState({
          deliveryNotes: jobDeliveryNotes.deliveryNotes,
          transportCost: jobDeliveryNotes.transportCost,
          disabled: jobDeliveryNotes.disabled,
          id: jobDeliveryNotes.id
        });
      }),
      map(jobDeliveryNotes => jobDeliveryNotes.deliveryNotes),
    );
  }

  @Action(UpdateDeliveryNotes)
  updateDeliveryNotes(ctx: StateContext<DeliveryNoteStateModel>, action: UpdateDeliveryNotes): Observable<DeliveryNote[]> {
    return this.deliveryNoteService.updateDeliveryNotes(action.jobId, action.request).pipe(
      tap(jobDeliveryNotes => {
        ctx.patchState({
          transportCost: jobDeliveryNotes.transportCost,
          disabled: jobDeliveryNotes.disabled,
          deliveryNotes: jobDeliveryNotes.deliveryNotes
        });
      }),
      map(jobDeliveryNotes => jobDeliveryNotes.deliveryNotes)
    );
  }

  @Action(CreateDeliveryNote)
  createDeliveryNote(ctx: StateContext<DeliveryNoteStateModel>, action: CreateDeliveryNote): Observable<DeliveryNote[]> {
    return this.deliveryNoteService.createDeliveryNote(action.jobId, action.request).pipe(
      tap(jobDeliveryNotes => {
        ctx.patchState({
          deliveryNotes: jobDeliveryNotes.deliveryNotes
        });
      }),
      map(jobDeliveryNotes => jobDeliveryNotes.deliveryNotes)
    );
  }

  @Action(DeleteDeliveryNote)
  deleteDeliveryNote(ctx: StateContext<DeliveryNoteStateModel>, action: DeleteDeliveryNote): Observable<DeliveryNote[]> {
    return this.deliveryNoteService.deleteDeliveryNote(action.jobId, action.id).pipe(
      tap(jobDeliveryNotes => {
        ctx.patchState({
          deliveryNotes: jobDeliveryNotes.deliveryNotes
        });
      }),
      map(jobDeliveryNotes => jobDeliveryNotes.deliveryNotes)
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<DeliveryNoteStateModel>): void {
    ctx.setState({
      deliveryNotes: [],
      transportCost: null,
      disabled: false,
      id: null,
    });
  }
}
